import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import ParticlesBackground from './particle/ParticlesBackground';
import Loader from './components/Loader';
import SideBar from './components/SideBar';
import ScrollProgress from './components/ScrollBar';
import Cursor from './Cursor';
import Home from './pages/Home/Index1.js';
import Admin from './pages/admin';
import LoginWithCode from './pages/auth/LoginWithCode.js';
import About from './pages/Home/About.js';
import Experiences from './pages/Home/Experiences.js';
import Projects from './pages/Home/Projects.js';
import Certificates from './pages/Home/Certificates.js';
import Contact from './pages/Home/Contact.js';
import Introduction from './pages/Home/Introduction.js';
import PageNull from './pages/Home/PageNull.js';
import NoConnection from './pages/Home/NoConnection.js';
import { ReloadData, setPortfolioData, getIntro } from './redux/rootSlice';

function App() {
  const dispatch = useDispatch();
  const { loading, portfolioData, reloadData } = useSelector(state => state.auth || {});
  const [scaling, setScaling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [introLoaded, setIntroLoaded] = useState(false); // New state for intro loading
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const handleOffline = () => {
    setIsOnline(false);
  };

  const handleOnline = () => {
    setIsOnline(true);
  };

  const getPortfolioData = async () => {
    const startTime = Date.now();
    try {
      const response = await axios.get(`${backend}/api/portfolio/get-portfolio-data`);
      const requestTime = Date.now() - startTime;
  
      if (requestTime > 4000) {
        toast.warning('Your network is slow.');
      }
  
      dispatch(setPortfolioData(response.data));
      dispatch(ReloadData(true));
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setIsLoading(false);
    }
  };

  // Load intro data first
  useEffect(() => {
    const loadIntroData = async () => {
      await dispatch(getIntro()); // Dispatch getIntro first
      setIntroLoaded(true); // Set introLoaded to true once it completes
    };
    loadIntroData();
  }, [dispatch]);

  useEffect(() => {
    if (introLoaded && isOnline) {
      getPortfolioData();
    } else if (!isOnline) {
      setIsLoading(false);
    }
  }, [introLoaded, isOnline]);

  useEffect(() => {
    if (reloadData && introLoaded) {
      getPortfolioData();
    }
  }, [reloadData, introLoaded]);

  useEffect(() => {
    const timer = setTimeout(() => {
      toast.warning('Your network is slow.');
    }, 60000); // 1-minute threshold
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <Router>
      <div className="relative min-h-screen">
        <ToastContainer />
        <div className="absolute inset-0 z-negative">
          <ParticlesBackground />
        </div>
        <div>
          <ScrollProgress />
          <Cursor scaling={scaling} />
          <SideBar />
          {loading || isLoading ? (
            <Loader />
          ) : (
            <Routes>
              {isOnline ? (
                <>
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/loginwithcode/:email" element={<LoginWithCode />} />
                  <Route path="/" element={<Introduction />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/experiences" element={<Experiences />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/certificates" element={<Certificates />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="*" element={<PageNull />} />
                </>
              ) : (
                <Route path="*" element={<Navigate to="/no-connection" replace />} />
              )}
              <Route path="/no-connection" element={<NoConnection />} />
            </Routes>
          )}
        </div>
      </div>
    </Router>
  );
}

export default App;
